import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import '../styles/ActivitiesPage.css';
import ActivityDrawer from './ActivityDrawer';
import { trackActivityStartClicked } from '../tracking';
import { getApiUrl } from '../config';
const API_BASE_URL = getApiUrl();
const ActivitiesPage = ({ activitiesUrl, onComplete, joinCode, sessionId, eventSlug, onActivityStart, }) => {
    const [activitiesData, setActivitiesData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const carouselRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [currentActivityUrl, setCurrentActivityUrl] = useState('');
    const [currentActivityWaitFor, setCurrentActivityWaitFor] = useState();
    useEffect(() => {
        const fetchActivities = async () => {
            try {
                setLoading(true);
                const response = await fetch(activitiesUrl);
                if (!response.ok) {
                    throw new Error('Failed to load activities');
                }
                const data = await response.json();
                setActivitiesData(data);
                setError(null);
            }
            catch (err) {
                setError('Unable to load activities. Please try again.');
                console.error('Error loading activities:', err);
            }
            finally {
                setLoading(false);
            }
        };
        fetchActivities();
    }, [activitiesUrl]);
    const updateScrollButtons = () => {
        if (carouselRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };
    useEffect(() => {
        const carousel = carouselRef.current;
        if (carousel) {
            carousel.addEventListener('scroll', updateScrollButtons);
            // Initial check
            updateScrollButtons();
            return () => carousel.removeEventListener('scroll', updateScrollButtons);
        }
    }, [activitiesData]);
    const scrollCarousel = (direction) => {
        if (carouselRef.current) {
            const scrollAmount = 380; // Card width + gap
            const newScrollLeft = direction === 'left'
                ? carouselRef.current.scrollLeft - scrollAmount
                : carouselRef.current.scrollLeft + scrollAmount;
            carouselRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth',
            });
        }
    };
    const resolveActivityLink = (link) => {
        if (!link)
            return undefined;
        // If link is a string, return it directly
        if (typeof link === 'string') {
            return link;
        }
        // If link is an object, resolve based on environment
        const env = process.env.NODE_ENV || 'development';
        if (env === 'development') {
            // In dev, prefer e2e link
            return link.e2e || link.qal || link.prd;
        }
        return link[env];
    };
    const recordGamePlay = async (activityTitle) => {
        if (!sessionId || !joinCode || !activityTitle) {
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/v1/gamePlays`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessionId,
                    eventSlug,
                    joinCode,
                    activityTitle,
                }),
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(data?.error?.message || 'Failed to record gameplay');
            }
        }
        catch (error) {
            console.error('Gameplay tracking error:', error);
        }
    };
    const handleActivityClick = (activityTitle, link, waitFor) => {
        onActivityStart?.(activityTitle);
        trackActivityStartClicked(activityTitle, joinCode);
        void recordGamePlay(activityTitle);
        const resolvedLink = resolveActivityLink(link);
        if (resolvedLink) {
            setCurrentActivityUrl(resolvedLink);
            setCurrentActivityWaitFor(waitFor);
            setDrawerOpen(true);
        }
    };
    const handleDrawerClose = () => {
        setDrawerOpen(false);
        setCurrentActivityUrl('');
        setCurrentActivityWaitFor(undefined);
    };
    if (loading) {
        return (_jsx("div", { className: "activities-page", children: _jsx("div", { className: "activities-loading", children: "Loading activities..." }) }));
    }
    if (error || !activitiesData) {
        return (_jsx("div", { className: "activities-page", children: _jsx("div", { className: "activities-error", children: error || 'Activities not found' }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "activities-page", children: _jsxs("div", { className: "activities-content", children: [_jsxs("div", { className: "activities-header", children: [_jsx("h1", { className: "activities-title", children: "Activities" }), _jsx("p", { className: "activities-description", children: "Complete 1 or more activities below as directed by your teacher to build your financial literacy skills." }), activitiesData.activities.length > 3 && (_jsxs("div", { className: "carousel-nav", children: [_jsx("button", { className: `carousel-nav-btn ${!canScrollLeft ? 'carousel-nav-btn-disabled' : ''}`, onClick: () => scrollCarousel('left'), disabled: !canScrollLeft, "aria-label": "Previous activities", children: _jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M15 18L9 12L15 6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }) }), _jsx("button", { className: `carousel-nav-btn ${!canScrollRight ? 'carousel-nav-btn-disabled' : ''}`, onClick: () => scrollCarousel('right'), disabled: !canScrollRight, "aria-label": "Next activities", children: _jsx("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M9 18L15 12L9 6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }) })] }))] }), _jsx("div", { className: "activities-carousel-wrapper", children: _jsx("div", { className: "activities-carousel", ref: carouselRef, children: activitiesData.activities.map((activity, index) => (activity.type === 'activity' ? (_jsxs("div", { className: "activity-card", children: [_jsxs("div", { className: "activity-card-image", children: [activity.image && (_jsx("img", { src: activity.image, alt: activity.title })), activity.category && (_jsx("span", { className: "activity-category", style: {
                                                        backgroundColor: activity.category.backgroundColor,
                                                        color: activity.category.textColor,
                                                    }, children: activity.category.title }))] }), _jsxs("div", { className: "activity-card-content", children: [_jsx("h3", { className: "activity-card-title", children: activity.title }), _jsx("p", { className: "activity-card-description", children: activity.description }), _jsxs("div", { className: "activity-card-footer", children: [activity.duration && (_jsxs("div", { className: "activity-duration", children: [_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }), _jsx("path", { d: "M8 4.5V8L10.5 9.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" })] }), _jsx("span", { children: activity.duration })] })), _jsx("button", { className: "activity-start-btn", onClick: () => handleActivityClick(activity.title, activity.link, activity.waitFor), children: "Start" })] })] })] }, index)) : (_jsxs("div", { className: "completion-card", children: [_jsx("h3", { className: "completion-card-title", children: activity.title }), _jsx("p", { className: "completion-card-description", children: activity.description }), _jsx("button", { className: "completion-btn", onClick: onComplete, children: "Next" })] }, index)))) }) })] }) }), _jsx(ActivityDrawer, { isOpen: drawerOpen, activityUrl: currentActivityUrl, waitFor: currentActivityWaitFor, onClose: handleDrawerClose })] }));
};
export default ActivitiesPage;
